import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { clamp, easeInOutSine, easeOutCubic, smoothstep } from '@/lib/uploader/math'
import { MiniFileGlyph } from '@/components/uploader/file-glyph'

export interface RowView {
    id: string
    name: string
    size: string
    tag: string
    color: string
    leaving: boolean
    leaveDelay: number
}

export interface UploadState {
    progress: number
    status: 'waiting' | 'uploading' | 'done' | 'failed'
}

interface UploadRowProps {
    row: RowView
    slot: number
    read: (id: string) => UploadState
    onSettled: (id: string, ok: boolean) => void
}

type Phase = 'uploading' | 'done' | 'failed'

// Fast at first, a pause around 70%, then a quick finish: 1.8 seconds end to end.
const progressCurve = (elapsed: number): number => {
    const t = elapsed / 1000

    if (t <= 0) return 0
    if (t < 0.75) return 0.68 * easeOutCubic(t / 0.75)
    if (t < 1.25) return 0.68 + 0.04 * easeInOutSine((t - 0.75) / 0.5)
    if (t < 1.8) return 0.72 + 0.28 * smoothstep(0, 1, (t - 1.25) / 0.55)

    return 1
}

const ROW_STEP = 74

const CheckMark: FC = (): ReactNode => {
    return (
        <span className='nbn-check-pop grid size-[22px] place-items-center rounded-full bg-swish'>
            <svg viewBox='0 0 22 22' className='size-[22px]' fill='none' aria-hidden='true'>
                <path
                    d='M6.6 11.3L9.7 14.3L15.4 8.2'
                    pathLength={1}
                    className='nbn-check-draw stroke-white'
                    strokeWidth={2}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
            </svg>
        </span>
    )
}

/*
 * One upload. The bar follows a scripted curve so it always feels the same, but never
 * runs ahead of the real upload: a slow connection simply holds the bar where it is.
 */
export const UploadRow: FC<UploadRowProps> = ({ row, slot, read, onSettled }): ReactNode => {
    const fillRef = useRef<HTMLDivElement>(null)
    const callbacks = useRef({ read, onSettled })
    const [phase, setPhase] = useState<Phase>('uploading')

    useEffect(() => {
        callbacks.current = { read, onSettled }
    })

    useEffect(() => {
        let frame = 0
        let shown = 0
        let last = performance.now()
        const started = last

        const tick = (now: number): void => {
            const state = callbacks.current.read(row.id)
            const dt = clamp((now - last) / 1000, 0, 0.05)

            last = now

            if (state.status === 'failed') {
                setPhase('failed')
                callbacks.current.onSettled(row.id, false)

                return
            }

            const goal = Math.min(progressCurve(now - started), state.progress)

            shown += (goal - shown) * (1 - Math.exp(-dt / 0.06))

            if (state.status === 'done' && goal >= 1 && shown > 0.995) shown = 1

            if (fillRef.current) fillRef.current.style.transform = `translateX(${((shown - 1) * 100).toFixed(2)}%)`

            if (shown >= 1) {
                setPhase('done')
                callbacks.current.onSettled(row.id, true)

                return
            }

            frame = requestAnimationFrame(tick)
        }

        frame = requestAnimationFrame(tick)

        return () => cancelAnimationFrame(frame)
    }, [row.id])

    const label = phase === 'done' ? "Uploaded" : phase === 'failed' ? "Failed" : "Uploading..."

    return (
        <div
            className='absolute bottom-[35px] left-[28px] h-[64px] w-[484px] transition-[transform,opacity] duration-[460ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-opacity'
            style={{
                transform: `translateY(${-slot * ROW_STEP + (row.leaving ? 6 : 0)}px)`,
                opacity: row.leaving || slot > 1 ? 0 : 1,
                transitionDelay: row.leaving ? `${row.leaveDelay}ms` : '0ms'
            }}
        >
            <div className='nbn-row-in h-full'>
                <div
                    className={cn(
                        'flex h-full items-center gap-3 rounded-[14px] bg-white px-4 shadow-[0_1px_2px_rgba(17,20,24,0.05),0_10px_24px_-14px_rgba(17,20,24,0.25)]',
                        phase === 'failed' && 'nbn-shake'
                    )}
                >
                    <MiniFileGlyph tag={row.tag} color={row.color} />

                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-[length:max(13px,calc(8.5px/var(--s,1)))] font-semibold leading-[1.25] text-ink'>
                            {row.name}
                        </p>

                        <p className='mt-0.5 text-[length:max(11px,calc(8.5px/var(--s,1)))] leading-[1.3] text-graphite'>
                            {row.size}
                        </p>
                    </div>

                    <div className='w-[124px] shrink-0'>
                        <p
                            key={phase}
                            className={cn(
                                'nbn-fade-in text-[length:max(11px,calc(8.5px/var(--s,1)))] leading-[1.2]',
                                phase === 'done' ? 'text-swish' : phase === 'failed' ? 'text-miss' : 'text-graphite'
                            )}
                        >
                            {label}
                        </p>

                        <div className='mt-1.5 h-1 overflow-hidden rounded-full bg-track'>
                            <div
                                ref={fillRef}
                                className={cn('h-full w-full rounded-full transition-colors duration-300', phase === 'failed' ? 'bg-miss' : 'bg-swish')}
                                style={{ transform: 'translateX(-100%)' }}
                            />
                        </div>
                    </div>

                    <div className='grid size-[22px] shrink-0 place-items-center'>
                        {phase === 'done' && <CheckMark />}
                    </div>
                </div>
            </div>
        </div>
    )
}
